import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef, ViewChild, inject, model, output, viewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { MonitoringService } from '@myapp/modules/fwcore/services/monitoring.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { FooterComponent } from './modules/fwcore/components/footer/footer.component';
import { MenuVerticalComponent } from './modules/fwcore/components/menu-vertical/menu-vertical.component';
import { TopbarComponent } from './modules/fwcore/components/topbar/topbar.component';

// Script for adding Clarity
const clarityScript = (clarityKey: string) =>
  '(function (c, l, a, r, i, t, y) { c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }; t = l.createElement(r);'
  + ' t.async = 1; t.src = "https://www.clarity.ms/tag/" + i; y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);'
  + '})(window, document, "clarity", "script", "' + clarityKey + '");';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('panelInOut', [
      transition('void => *', [
        style({ transform: 'translateX(-100%)' }),
        animate(400)
      ]),
      transition('* => void', [
        animate(400, style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ],
  standalone: true,
  imports: [CommonModule, DialogModule, RouterOutlet, TopbarComponent, MenuVerticalComponent, FooterComponent]
})
export class AppComponent implements OnInit {

  // ---------------- State properties ----------------

  public showTopbar: boolean = false;
  public showMenubar: boolean = false;
  public showFooter: boolean = false;
  public isLoading: boolean = false;
  public menuHidden: boolean = false;

  protected impersonationEmail = model('');
  public impersonateChanged = output<string>();
  protected compMenuVertical = viewChild<MenuVerticalComponent>('lhsMenu');

  // ---------------- Idle settings ----------------

  public idleLength: number = 30;
  public idleCountdown: number = 30;
  public idleTimeout: number = 30;

  private pageStart: number = 0;
  private modalService = inject(NgbModal);

  @ViewChild('idleContent') idleContent?: TemplateRef<any>;

  private updateLastActivity(): void {
    localStorage.setItem(
      'lastActivityTime',
      Date.now().toString()
    );
  }

  private isSessionExpired(): boolean {
    const lastActivity =
      Number(localStorage.getItem('lastActivityTime'));
    if (!lastActivity) {
      return false;
    }
    return (
      Date.now() - lastActivity
    ) >= (this.idleLength + this.idleTimeout) * 1000;
  }

  // ---------------- Constructor ----------------

  constructor(
    private router: Router,
    private monitoring: MonitoringService,
    @Inject('CONFIG_SETTINGS') private configSettings: any,   // no modifier — constructor body only
    private _idle: Idle,
    private wrapper: OktaWrapperService
  ) {
    const idleSettings = configSettings[<any>'idle'];
    this.idleLength = idleSettings[<any>'length'];
    this.idleCountdown = idleSettings[<any>'countdown'];
    this.idleTimeout = idleSettings[<any>'timeout'];

    // Subscribe to page events for monitoring and idle reset
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        monitoring.startNavigationEvent(event.url);
        this.pageStart = +new Date();

        // Reset idle timer on navigation — Angular route changes do not
        // trigger DOM events so DEFAULT_INTERRUPTSOURCES won't catch them
        this._idle.interrupt();

      } else if (event instanceof NavigationEnd) {
        monitoring.endNavigationEvent(event.url);

        // Only log page view when authenticated and token is present.
        // Avoids AuthSdkError when navigating to login page before token exists.
        const token = this.wrapper.getAccessToken();
        if (token) {
          const userClaim = this.wrapper.getUser();   // synchronous — reads heldToken
          const user = userClaim?.email ?? '';
          monitoring.logPageView(event.url, user, token);
        }
      }
    });

    // Handle page layout and idle state based on route
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        const isFullscreen =
          event['url'] === '/login/callback' ||
          event['urlAfterRedirects'] === '/login' ||
          event['url'] === '/login' ||
          event['urlAfterRedirects'] === '/error/404' ||
          event['url'] === '/error/404' ||
          event['urlAfterRedirects'] === '/error/500' ||
          event['url'] === '/error/500' ||
          event['urlAfterRedirects'] === '/error/not-authorized' ||
          event['url'] === '/error/not-authorized';

        // Menu bar depends on license/permissions data the backend can't
        // resolve for an unlicensed user — keep topbar/footer but suppress
        // the menu panel so app-menu-vertical never mounts on this route.
        const isNoLicense =
          event['url'] === '/no-license' ||
          event['urlAfterRedirects'] === '/no-license';

        this.showTopbar = !isFullscreen;
        this.showMenubar = !isFullscreen && !isNoLicense;
        this.showFooter = !isFullscreen;

        // Idle timer is only active on authenticated routes.
        // Stops on login/error pages — prevents modal showing when not logged in.
        if (isFullscreen) {
          this._idle.stop();
        } else if (this.wrapper.isAuthenticatedSync()) {
          this._idle.watch();
        }

        const layout = document.querySelector('.isg-page-layout');
        if (isFullscreen) {
          layout?.classList.add('fullscreen');
        } else {
          layout?.classList.remove('fullscreen');
        }
      }
    });

    // Load MS Clarity script if configured
    try {
      const claritySettings = configSettings[<any>'clarity'];
      const clarityKey = claritySettings[<any>'key'];
      if (clarityKey && clarityKey.length > 0) {
        const clarityScriptTag = document.createElement('script');
        clarityScriptTag.type = 'text/javascript';
        clarityScriptTag.async = true;
        clarityScriptTag.innerHTML = clarityScript(clarityKey);
        document.head.appendChild(clarityScriptTag);
      }
    } catch (error) {
      console.log('Error loading Clarity script', error);
    }

    this.updateLastActivity();

    window.addEventListener('click', () => {
      this.updateLastActivity();
    });
    
    window.addEventListener('keydown', () => {
      this.updateLastActivity();
    });

    document.addEventListener('visibilitychange', () => {
      console.log(
        'VISIBILITY:',
        document.visibilityState,
        new Date()
      );
      if (
        document.visibilityState === 'visible' &&
        this.isSessionExpired()
      ) {
        console.log(
          'Session expired while hidden'
        );
        this.logout();
      }
    });

    window.addEventListener('focus', () => {
      console.log('WINDOW FOCUS', new Date());
      if (this.isSessionExpired())
      {
        console.log(
          'Session expired while focus'
        );
        this.logout();
      }
    });
  }

  // ---------------- ng ----------------

  ngOnInit(): void {

    // Stop idle timer on logout
    this.wrapper.loggedOut.subscribe(() => {
      this._idle.stop();
    });

    // Configure idle timer
    this._idle.setIdle(this.idleLength);
    this._idle.setTimeout(this.idleTimeout);
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Show idle warning modal when user becomes idle.
    // backdrop: 'static' and keyboard: false prevent accidental dismissal
    // by clicking outside the modal or pressing Escape.
    this._idle.onIdleStart.subscribe(() => {
      this._idle.clearInterrupts();

      const modal = this.modalService.open(this.idleContent, {
        centered: true,
        backdrop: 'static',  // ✅ prevent accidental dismiss by clicking outside
        keyboard: false       // ✅ prevent dismiss by pressing Escape
      });

      modal.result.then(
        () => {
          // Modal closed by logout button
          this.logout();
        },
        () => {
          // Modal dismissed by cancel button — user chose to continue
          this._idle.stop();
          this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
          this._idle.watch();
        }
      );
    });

    // Update countdown display every second during timeout window
    this._idle.onTimeoutWarning.subscribe((secondsLeft: number) => {
      this.idleCountdown = secondsLeft;
    });

    // Timeout reached — log user out
    this._idle.onTimeout.subscribe(() => {
      this.modalService.dismissAll();
      this.logout();
    });

    // Start or stop idle timer based on authentication state changes.
    // Does not start idle here — route events handle when to watch.
    this.wrapper.authenticationChanged.subscribe((auth: boolean) => {
      console.log('Authentication state changed: ' + auth);
      if (!auth) {
        this._idle.stop();
      }
    });
  }

  // ---------------- Actions ----------------

  /** Toggles the left hand side menu visibility. */
  public toggleMenu(): void {
    this.menuHidden = !this.menuHidden;
  }

  /** Handles impersonation email change — refreshes menu and emits event. */
  protected onImpersonateChanged(email: string): void {
    this.impersonationEmail.set(email);
    this.impersonateChanged.emit(email);
    this.compMenuVertical()?.refreshMenu();
  }

  /** Logs the user out via Okta. */
  private logout(): void {
    this.wrapper.logout();
  }
}
